import { useQuery } from '@tanstack/react-query';
import { fetchHistoricalResults } from '@/api/vercel';

/**
 * Hook for fetching historical data by cycling through all tracks
 * This approach fetches data per track to get comprehensive coverage
 */
export const useBulkTrackData = (filters = {}, options = {}) => {
  const { enabled = true, maxTracks = 50 } = options;

  return useQuery({
    queryKey: ['bulk-track-data', filters, maxTracks],
    queryFn: async () => {
      try {
        console.log('Starting bulk track-by-track data fetch...');
        
        // Load track names to get all tracks
        const trackNamesResponse = await fetch('/data/horseracing/tracks/track-names.json');
        if (!trackNamesResponse.ok) {
          throw new Error('Failed to load track names');
        }
        const trackNames = await trackNamesResponse.json();
        
        // Get track names, prioritizing major tracks first
        const majorTracks = [
          'Flemington', 'Caulfield', 'Randwick', 'Rosehill', 'Doomben', 'Eagle Farm',
          'Morphettville', 'Ascot', 'Belmont Park', 'Moonee Valley', 'Canterbury Park',
          'Warwick Farm', 'Gold Coast', 'Sunshine Coast', 'Ipswich', 'Cranbourne',
          'Sandown', 'Ballarat', 'Geelong', 'Bendigo'
        ];
        
        // Get all track names and prioritize major ones
        const allTrackNames = Object.keys(trackNames);
        const prioritizedTracks = [
          ...majorTracks.filter(track => allTrackNames.includes(track)),
          ...allTrackNames.filter(track => !majorTracks.includes(track))
        ].slice(0, maxTracks);
        
        console.log(`Attempting to fetch data for ${prioritizedTracks.length} tracks...`);
        
        const allRacesData = [];
        const trackResults = [];
        let successfulFetches = 0;
        let failedFetches = 0;
        
        // Try different date ranges to get more data
        const dateRanges = [
          null, // No date filter
          // Could add specific date ranges here if needed
        ];
        
        for (const dateRange of dateRanges) {
          // Process tracks in smaller batches to avoid overwhelming the API
          const batchSize = 3;
          
          for (let i = 0; i < prioritizedTracks.length; i += batchSize) {
            const batch = prioritizedTracks.slice(i, i + batchSize);
            
            // Fetch data for each track in the batch
            const batchPromises = batch.map(async (trackName) => {
              try {
                // Try to fetch historical results
                // Since we can't specify track in the API, we'll fetch general data
                // and filter it for the specific track
                const response = await fetchHistoricalResults(dateRange ? { date: dateRange } : {});
                
                if (response.status === 'success' && response.data) {
                  // Filter data for this specific track
                  const trackData = Object.entries(response.data)
                    .filter(([raceId, raceData]) => {
                      // Extract track name from race ID
                      const parts = raceId.split('-');
                      if (parts.length < 4) return false;
                      
                      const raceTrackName = parts.slice(0, parts.length - 4).join(' ');
                      const trackInfo = trackNames[trackName];
                      
                      // Check if this race belongs to the current track
                      // Use exact matching to prevent substring matches (e.g., "warwick" matching when looking for "warwick farm")
                      return raceTrackName.toLowerCase() === trackName.toLowerCase() ||
                             raceTrackName.toLowerCase() === trackInfo?.alt?.toLowerCase();
                    })
                    .map(([raceId, raceData]) => ({
                      ...raceData,
                      id: raceId,
                      trackName: trackName,
                      state: trackNames[trackName]?.State || 'Unknown'
                    }));
                  
                  return {
                    trackName,
                    races: trackData,
                    success: true
                  };
                }
                
                return {
                  trackName,
                  races: [],
                  success: false,
                  error: 'No data returned'
                };
              } catch (error) {
                console.warn(`Failed to fetch data for track ${trackName}:`, error.message);
                return {
                  trackName,
                  races: [],
                  success: false,
                  error: error.message
                };
              }
            });
            
            // Wait for batch to complete
            const batchResults = await Promise.allSettled(batchPromises);
            
            // Process batch results
            batchResults.forEach((result) => {
              if (result.status === 'fulfilled') {
                const trackResult = result.value;
                trackResults.push(trackResult);
                
                if (trackResult.success && trackResult.races.length > 0) {
                  allRacesData.push(...trackResult.races);
                  successfulFetches++;
                } else {
                  failedFetches++;
                }
              } else {
                failedFetches++;
              }
            });
            
            // Add a small delay between batches to be respectful to the API
            if (i + batchSize < prioritizedTracks.length) {
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }
          
          // If we got some data, we can break early
          if (allRacesData.length > 0) {
            break;
          }
        }
        
        console.log(`Bulk track fetch completed: ${successfulFetches} successful, ${failedFetches} failed`);
        console.log(`Total races collected: ${allRacesData.length}`);
        
        // Remove duplicates based on race ID
        const uniqueRaces = allRacesData.filter((race, index, self) => 
          index === self.findIndex(r => r.id === race.id)
        );
        
        console.log(`Unique races after deduplication: ${uniqueRaces.length}`);
        
        return {
          races: uniqueRaces,
          totalCount: uniqueRaces.length,
          originalCount: allRacesData.length,
          trackResults,
          fetchStats: {
            totalTracks: prioritizedTracks.length,
            successfulFetches,
            failedFetches,
            tracksWithData: trackResults.filter(t => t.success && t.races.length > 0).length
          }
        };
      } catch (error) {
        console.error('Error in bulk track data fetch:', error);
        throw error;
      }
    },
    enabled,
    staleTime: 20 * 60 * 1000, // 20 minutes (longer since this is expensive)
    gcTime: 4 * 60 * 60 * 1000, // 4 hours cache time
    retry: 1, // Only retry once since this is expensive
    retryDelay: 5000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export default useBulkTrackData;